import classNames from 'classnames'
import type { UseFormReturn } from 'react-hook-form'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomTextarea from '@/components/Atoms/AtomTextarea/AtomTextarea'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import type {
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  SelectFormControlType,
} from '@/types'
import handleChangeBarcodeInput from '@/utils/forms/handleChangeBarcodeInput'
import handleChangeNumberInput from '@/utils/forms/handleChangeNumberInput'

import type { NomenclatureDictionaries } from '../hooks/useNomenclatureDictionaries'
import type { NomenclatureCreationType } from '../SingularImport.config'
import BrandField from './BrandField'
import cn from './NomenclatureCreationForm.module.scss'
import SupplierArticlesField from './SupplierArticlesField'

type NomenclatureCreationFormProps = {
  form: UseFormReturn<NomenclatureCreationType>
  dictionaries: NomenclatureDictionaries
  isOrganisationSelected: boolean
}

const MEASUREMENT_FIELDS: Array<{
  name: FormInputNameType
  title: string
  isFractional: boolean
}> = [
  { name: 'itemWeight', title: 'Вес, кг', isFractional: true },
  { name: 'itemLength', title: 'Длина, см', isFractional: true },
  { name: 'itemWidth', title: 'Ширина, см', isFractional: true },
  { name: 'itemHeight', title: 'Высота, см', isFractional: true },
  { name: 'warranty', title: 'Гарантия, мес.', isFractional: false },
]

function NomenclatureCreationForm({
  form,
  dictionaries,
  isOrganisationSelected,
}: NomenclatureCreationFormProps) {
  const { control, register, formState } = form
  const inputProps = {
    layout: 'stacked' as const,
    location: 'singularNomenclatureImport' as const,
    isOrganisationSelected,
    errors: formState.errors,
    control: control as FormInputControlType,
    register: register as FormInputRegisterType,
  }

  return (
    <div
      className={classNames(
        cn.form,
        isOrganisationSelected && cn.accentSelected
      )}
    >
      <AtomFormInput {...inputProps} name="article" title="Артикул" required />
      <SupplierArticlesField
        form={form}
        isOrganisationSelected={isOrganisationSelected}
      />
      <AtomFormInput
        {...inputProps}
        name="description"
        title="Наименование"
        required
      />
      <BrandField
        form={form}
        dictionaries={dictionaries}
        isOrganisationSelected={isOrganisationSelected}
      />
      <AtomFormInput
        {...inputProps}
        name="barcode"
        title="Штрихкод"
        handleChangeInput={handleChangeBarcodeInput}
      />
      {MEASUREMENT_FIELDS.map((field) => (
        <AtomFormInput
          {...inputProps}
          key={field.name}
          name={field.name}
          title={field.title}
          inputMode="decimal"
          handleChangeInput={(event) =>
            handleChangeNumberInput(event, field.isFractional)
          }
        />
      ))}
      <div onMouseEnter={dictionaries.loadCountries}>
        <HoverElement>
          <AtomSelect
            layout="stacked"
            fullWidth
            openByHover={false}
            showSearch
            title="Страна происхождения"
            placeholder="Выберите страну"
            isOrganisationSelected={isOrganisationSelected}
            options={dictionaries.countries.map((country) => ({
              value: country.id,
              label: country.description,
            }))}
            formName="countryOfOrigin"
            formControl={control as SelectFormControlType}
            formErrors={formState.errors}
          />
        </HoverElement>
      </div>
      <div onMouseEnter={dictionaries.loadUnits}>
        <HoverElement>
          <AtomSelect
            layout="stacked"
            fullWidth
            openByHover={false}
            showSearch
            title="Единица измерения"
            placeholder="Выберите единицу"
            isOrganisationSelected={isOrganisationSelected}
            options={dictionaries.units.map((unit) => ({
              value: unit.id,
              label: unit.description,
            }))}
            formName="unitOfMeasurement"
            formControl={control as SelectFormControlType}
            formErrors={formState.errors}
          />
        </HoverElement>
      </div>
      <AtomTextarea
        id="nomenclature-details"
        title="Описание"
        registration={register('details')}
        error={formState.errors.details?.message}
        isOrganisationSelected={isOrganisationSelected}
      />
      <AtomTextarea
        id="nomenclature-comment"
        title="Комментарий"
        registration={register('comment')}
        error={formState.errors.comment?.message}
        isOrganisationSelected={isOrganisationSelected}
      />
    </div>
  )
}

export default NomenclatureCreationForm
